import React, { useState, useEffect, useCallback } from 'react';
import { api, formatApiError } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Link } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { MatchingToolbar, UnmatchedPanel, MatchedTable } from '../components/matching/MatchingComponents';

export default function Matching() {
  const [transactions, setTransactions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState('all');
  const [autoMatching, setAutoMatching] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [matchingInvoice, setMatchingInvoice] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [transactionsRes, invoicesRes, clientsRes] = await Promise.all([
        api.get('/bank-transactions'), api.get('/invoices'), api.get('/clients'),
      ]);
      setTransactions(transactionsRes.data); setInvoices(invoicesRes.data); setClients(clientsRes.data);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAutoMatch = async () => {
    if (selectedClient === 'all') { toast.error('Please select a client first'); return; }
    setAutoMatching(true);
    try { const { data } = await api.post(`/auto-match/${selectedClient}`); toast.success(data.message); fetchData(); }
    catch (error) { toast.error(formatApiError(error)); }
    finally { setAutoMatching(false); }
  };

  const handleManualMatch = async () => {
    if (!selectedTransaction || !matchingInvoice) { toast.error('Please select both a transaction and an invoice'); return; }
    try {
      await api.post('/match-transaction', { transaction_id: selectedTransaction, invoice_id: matchingInvoice });
      toast.success('Transaction matched to invoice'); setSelectedTransaction(null); setMatchingInvoice(null); fetchData();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const getClientName = (clientId) => clients.find((c) => c.id === clientId)?.name || 'Unknown';
  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'GBP' }).format(amount || 0);

  const handleExport = async (endpoint, filename) => {
    setExporting(true);
    try {
      const params = selectedClient !== 'all' ? `?client_id=${selectedClient}` : '';
      const response = await api.get(`/export/${endpoint}${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('CSV exported successfully!');
    } catch { toast.error('Failed to export CSV'); }
    finally { setExporting(false); }
  };

  const unmatchedTransactions = transactions.filter(t => t.status === 'unmatched' && (selectedClient === 'all' || t.client_id === selectedClient));
  const pendingInvoices = invoices.filter(i => i.status === 'pending' && (selectedClient === 'all' || i.client_id === selectedClient));
  const matchedTransactions = transactions.filter(t => t.status === 'matched' && (selectedClient === 'all' || t.client_id === selectedClient));

  return (
    <div className="space-y-6" data-testid="matching-page">
      <MatchingToolbar
        clients={clients} selectedClient={selectedClient} setSelectedClient={setSelectedClient}
        autoMatching={autoMatching} onAutoMatch={handleAutoMatch} matchedCount={matchedTransactions.length}
        exporting={exporting}
        onExportMatched={() => handleExport('matched-transactions', 'matched_transactions')}
        onExportAll={() => handleExport('all-transactions', 'all_transactions')}
      />

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div></div>
      ) : (
        <>
          <UnmatchedPanel
            unmatchedTransactions={unmatchedTransactions} pendingInvoices={pendingInvoices}
            selectedTransaction={selectedTransaction} setSelectedTransaction={setSelectedTransaction}
            matchingInvoice={matchingInvoice} setMatchingInvoice={setMatchingInvoice}
            formatCurrency={formatCurrency}
          />
          {(selectedTransaction || matchingInvoice) && (
            <div className="flex justify-center">
              <Button onClick={handleManualMatch} disabled={!selectedTransaction || !matchingInvoice} className="px-8" data-testid="manual-match-btn">
                <Link className="w-4 h-4 mr-2" /> Match Selected Items
              </Button>
            </div>
          )}
          <MatchedTable matchedTransactions={matchedTransactions} invoices={invoices} getClientName={getClientName} formatCurrency={formatCurrency} />
        </>
      )}
    </div>
  );
}
